import React, { useState } from 'react'
import ReactDOM from 'react-dom'
import { getHeroDescriptionByName, HERO_DATA } from '../data/heroData'
import { isTouchDevice } from '../services/device'
import { Language, t } from '../data/translations'
import evelionAvatar from '../assets/custom/EvelionAvatar.png'
import corilAvatar from '../assets/custom/CorilAvatar.png'
import terrikAvatar from '../assets/custom/TerrikAvatar.png'

const ZoneInfoModal: React.FC<{
  zoneId: number
  factionName: string
  hero?: { name: string; originalName?: string; imageUrl?: string } | null
  points?: { harmonized: number; chaotic: number; untouched: number }
  onClose: () => void
  language: Language
}> = ({ zoneId, factionName, hero, points, onClose, language }) => {
  const portalRoot = document.getElementById('portal-root') || document.body
  const [hoveredHero, setHoveredHero] = useState<{ name: string, desc: string, card: string } | null>(null)

  // Get hero card from centralized data
  const getHeroCard = (name: string): string => {
    // Map Erok-Buru to Verg for player display
    const displayName = name === 'Erok-Buru' ? 'Verg' : name
    const normalizedId = displayName.toUpperCase().replace(/[\s-]/g, '_')
    const heroData = HERO_DATA[normalizedId]
    return heroData?.card || ''
  }

  // Map hero name for display (Erok-Buru → Verg)
  const mapHeroName = (name: string): string => name === 'Erok-Buru' ? 'Verg' : name

  const handleMouseEnter = (name: string, displayName?: string) => {
    // Only trigger on non-touch devices to prevent double-triggering
    if (isTouchDevice()) return
    const desc = getHeroDescriptionByName(name, language)
    const card = getHeroCard(name)
    const mapped = mapHeroName(name)
    const title = displayName || t(`hero.${mapped.toUpperCase().replace(/[\s-]/g, '_')}.name`, language) || mapped
    if (desc && card) {
      setHoveredHero({ name: title, desc, card })
    } else {
      setHoveredHero(null)
    }
  }

  const handleHeroClick = (e: React.MouseEvent, name: string, displayName?: string) => {
    // On touch devices, tap to show info (toggle behavior)
    if (isTouchDevice()) {
      e.stopPropagation()
      const mapped = mapHeroName(name)
      const title = displayName || t(`hero.${mapped.toUpperCase().replace(/[\s-]/g, '_')}.name`, language) || mapped
      if (hoveredHero?.name === title) {
        setHoveredHero(null) // Toggle off
      } else {
        const desc = getHeroDescriptionByName(name, language)
        const card = getHeroCard(name)
        if (desc && card) {
          setHoveredHero({ name: title, desc, card })
        }
      }
    }
  }

  return ReactDOM.createPortal(
    <div
      className="fixed inset-0 bg-black/70 flex items-center justify-center z-50"
      onClick={(e) => {
        e.stopPropagation()
        onClose()
      }}
    >
      <div
        className="bg-slate-800 rounded-lg shadow-2xl p-6 text-white border-2 border-slate-600 w-[520px] relative"
        onClick={(e) => e.stopPropagation()}
        onMouseLeave={() => setHoveredHero(null)}
      >
        <h2 className="text-2xl font-bold mb-2">{t('term.zone', language)} {zoneId}</h2>
        <div className="mb-4">{t('ui.faction', language)}: {t(`term.${factionName.toLowerCase()}`, language)}</div>
        {points && (
          <div className="mb-4 p-3 bg-slate-900/50 rounded-lg text-sm space-y-1">
            <div className="font-bold text-slate-300 mb-2">{t('ui.controlPoints', language)}:</div>
            <div className="flex justify-between">
              <span className="text-blue-400">{t('term.harmonized', language)}:</span>
              <span className="font-mono">{points.harmonized}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-red-400">{t('term.chaotic', language)}:</span>
              <span className="font-mono">{points.chaotic}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-white">{t('term.untouched', language)}:</span>
              <span className="font-mono">{points.untouched}</span>
            </div>
          </div>
        )}
        {/* Display Heroes */}
        <div className="mb-4 relative">
          <div className="font-bold text-slate-300 mb-2">{t('ui.heroes', language)}:</div>
          {[1, 2, 3].includes(zoneId) ? (
            // Home Zones: Show all faction heroes
            factionName === 'Untouched' ? (
              <div className="flex gap-4">
                <div className="flex flex-col items-center cursor-help" onMouseEnter={() => handleMouseEnter('Astrid')} onClick={(e) => handleHeroClick(e, 'Astrid')}>
                  <img src={new URL('../assets/custom/AstridAvatar.png', import.meta.url).href} className="w-16 h-16 rounded-full border-2 border-white hover:scale-110 transition-transform" />
                  <div className="text-sm mt-1">{t('hero.ASTRID.name', language)}</div>
                </div>
                <div className="flex flex-col items-center cursor-help" onMouseEnter={() => handleMouseEnter('Ferrim')} onClick={(e) => handleHeroClick(e, 'Ferrim')}>
                  <img src={new URL('../assets/custom/FerrimAvatar.png', import.meta.url).href} className="w-16 h-16 rounded-full border-2 border-white hover:scale-110 transition-transform" />
                  <div className="text-sm mt-1">{t('hero.FERRIM.name', language)}</div>
                </div>
              </div>
            ) : factionName === 'Harmonized' ? (
              <div className="flex gap-4">
                <div className="flex flex-col items-center cursor-help" onMouseEnter={() => handleMouseEnter('Norim')} onClick={(e) => handleHeroClick(e, 'Norim')}>
                  <img src={new URL('../assets/custom/NorimAvatar.png', import.meta.url).href} className="w-16 h-16 rounded-full border-2 border-blue-400 hover:scale-110 transition-transform" />
                  <div className="text-sm mt-1">{t('hero.NORIM.name', language)}</div>
                </div>
                <div className="flex flex-col items-center cursor-help" onMouseEnter={() => handleMouseEnter('Terrik')} onClick={(e) => handleHeroClick(e, 'Terrik')}>
                  <img src={terrikAvatar} className="w-16 h-16 rounded-full border-2 border-blue-400 hover:scale-110 transition-transform" />
                  <div className="text-sm mt-1">{t('hero.TERRIK.name', language)}</div>
                </div>
              </div>
            ) : factionName === 'Chaotic' ? (
              <div className="flex gap-4">
                <div className="flex flex-col items-center cursor-help" onMouseEnter={() => handleMouseEnter('Evelion')} onClick={(e) => handleHeroClick(e, 'Evelion')}>
                  <img src={evelionAvatar} className="w-16 h-16 rounded-full border-2 border-red-400 hover:scale-110 transition-transform" />
                  <div className="text-sm mt-1">{t('hero.EVELION.name', language)}</div>
                </div>
                <div className="flex flex-col items-center cursor-help" onMouseEnter={() => handleMouseEnter('Coril')} onClick={(e) => handleHeroClick(e, 'Coril')}>
                  <img src={corilAvatar} className="w-16 h-16 rounded-full border-2 border-red-400 hover:scale-110 transition-transform" />
                  <div className="text-sm mt-1">{t('hero.CORIL.name', language)}</div>
                </div>
              </div>
            ) : (
              <div className="text-slate-300">{t('ui.neutralZone', language)}</div>
            )
          ) : hero ? (
            // Captured Zones: Show specific hero
            <div className="flex items-center gap-4 cursor-help" onMouseEnter={() => handleMouseEnter(hero.originalName || hero.name, hero.name)} onClick={(e) => handleHeroClick(e, hero.originalName || hero.name, hero.name)}>
              {hero.imageUrl ? (
                <img src={hero.imageUrl} className="w-24 h-24 rounded-full object-cover hover:scale-110 transition-transform" />
              ) : (
                <div className="w-24 h-24 rounded-full bg-slate-700" />
              )}
              <div className="text-xl font-semibold">{hero.name}</div>
            </div>
          ) : (
            <div className="text-slate-300">{t('ui.noHeroBound', language)}</div>
          )}
        </div>

        {/* Hero Preview Overlay */}
        {hoveredHero && (
          <div className="absolute inset-0 z-50 pointer-events-none flex items-center justify-center bg-black/90 backdrop-blur-sm rounded-lg animate-[fadeIn_0.2s_ease-out]">
            <div className="flex items-center gap-6 p-6">
              <img src={hoveredHero.card} className="h-64 rounded-lg shadow-[0_0_20px_rgba(255,255,255,0.2)]" />
              <div className="max-w-xs text-left">
                <h3 className="text-2xl font-bold text-white mb-2">{hoveredHero.name}</h3>
                <p className="text-slate-300 text-sm leading-relaxed">{hoveredHero.desc}</p>
              </div>
            </div>
          </div>
        )}

        <div className="mt-6 text-right">
          <button className="px-4 py-2 rounded bg-slate-700 hover:bg-slate-600" onClick={onClose}>{t('ui.close', language)}</button>
        </div>
      </div>
    </div>,
    portalRoot
  )
}

export default ZoneInfoModal